import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  ChevronDownIcon,
  ClipboardListIcon,
  CollectionIcon,
  CogIcon,
  HomeIcon,
  LocationMarkerIcon,
  PhotographIcon,
  TemplateIcon,
  XIcon,
} from "@heroicons/react/outline";

const navLinkClass = ({ isActive }) =>
  `group flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition-all duration-200 ${isActive
    ? "bg-white/15 text-white shadow-lg shadow-sky-950/20"
    : "text-slate-300 hover:bg-white/10 hover:text-white"
  }`;

export default function Sidebar({ isOpen = false, onClose }) {
  const [openSection, setOpenSection] = useState(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const activeSection = {
      "/admin/tour-places": "homepage",
      "/admin/view-places": "homepage",
      "/admin/activities/add": "tours",
      "/admin/activities/fetch": "tours",
      "/admin/add-explore-images": "images",
      "/admin/tour-gallery": "images",
      "/admin/route-maps": "images",
      "/admin/view-tour-details": "trip-details",
      "/admin/complete-your-day": "trip-details",
    }[pathname];

    if (activeSection) setOpenSection(activeSection);
  }, [pathname]);

  const toggleSection = (section) => {
    setOpenSection((current) => (current === section ? null : section));
  };

  const handleNavigate = () => {
    if (window.innerWidth < 1024) onClose?.();
  };

  const sectionButtonClass = (section) =>
    `group flex w-full items-center justify-between rounded-xl px-3 py-3 text-left text-sm font-bold transition-all duration-200 ${openSection === section
      ? "bg-white/10 text-white"
      : "text-slate-300 hover:bg-white/10 hover:text-white"
    }`;

  const subLinkClass = ({ isActive }) =>
    `block rounded-lg px-3 py-2 text-sm transition-colors duration-200 ${isActive
      ? "bg-sky-400/15 font-medium text-sky-200"
      : "text-white hover:bg-white/10 hover:text-white"
    }`;

  return (
    <>
      {isOpen && (
        <button
          type="button"
          aria-label="Close navigation menu"
          className="fixed inset-0 z-40 bg-slate-950/50 backdrop-blur-sm lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 flex h-dvh flex-col overflow-hidden border-r border-white/10 bg-gradient-to-b from-slate-950 via-slate-900 to-sky-950 text-white shadow-2xl shadow-slate-950/30 transition-all duration-300 ease-out ${isOpen
            ? "w-72 translate-x-0 lg:w-64"
            : "w-72 -translate-x-full lg:w-20 lg:translate-x-0"
          }`}
      >
        <div className="flex h-20 shrink-0 items-center border-b border-white/10 px-4">
          <div className="flex min-w-0 flex-1 items-center gap-3 overflow-hidden">
      
            <div
              className={`min-w-0 transition-all duration-200 ${isOpen ? "translate-x-0 opacity-100" : "translate-x-2 opacity-0 lg:hidden"
                }`}
            >
              <p className="truncate text-sm text-white font-semibold tracking-wide">GO SEHARI</p>
              <p className="mt-0.5 text-[10px] font-medium uppercase tracking-[0.18em] text-sky-300">
                Admin Studio
              </p>
            </div>
          </div>
          <button
            type="button"
            aria-label="Close navigation menu"
            className="grid h-9 w-9 place-items-center rounded-lg text-slate-300 transition hover:bg-white/10 hover:text-white lg:hidden"
            onClick={onClose}
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-5 [scrollbar-width:thin]" aria-label="Admin navigation">
          <p className={`mb-3 px-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 ${!isOpen ? "lg:text-center lg:text-[0px]" : ""}`}>
            Workspace
          </p>

          <ul className="space-y-1">
            <li>
              <NavLink to="/admin/dashboard" onClick={handleNavigate} title="Dashboard" className={navLinkClass}>
                <HomeIcon className="h-5 w-5 shrink-0" />
                <span className={!isOpen ? "lg:hidden" : ""}>Dashboard</span>
              </NavLink>
            </li>

            <li>
              <button type="button" title="Home Page" onClick={() => toggleSection("homepage")} className={sectionButtonClass("homepage")} aria-expanded={openSection === "homepage"}>
                <span className="flex items-center gap-3"><TemplateIcon className="h-5 w-5 shrink-0" /><span className={!isOpen ? "lg:hidden" : ""}>Home Page</span></span>
                <ChevronDownIcon className={`h-4 w-4 shrink-0 transition-transform duration-200 ${openSection === "homepage" ? "rotate-180" : ""} ${!isOpen ? "lg:hidden" : ""}`} />
              </button>
              {isOpen && openSection === "homepage" && (
                <ul className="ml-5 mt-1 space-y-1 border-l border-white/10 py-1 pl-3 animate-[fadeIn_180ms_ease-out]">
                  <li><NavLink to="/admin/tour-places" onClick={handleNavigate} className={subLinkClass}>Headings</NavLink></li>
                  <li><NavLink to="/admin/view-places" onClick={handleNavigate} className={subLinkClass}>View Places</NavLink></li>
                </ul>
              )}
            </li>

            <li>
              <button type="button" title="Tours" onClick={() => toggleSection("tours")} className={sectionButtonClass("tours")} aria-expanded={openSection === "tours"}>
                <span className="flex items-center gap-3"><CollectionIcon className="h-5 w-5 shrink-0" /><span className={!isOpen ? "lg:hidden" : ""}>Tours</span></span>
                <ChevronDownIcon className={`h-4 w-4 shrink-0 transition-transform duration-200 ${openSection === "tours" ? "rotate-180" : ""} ${!isOpen ? "lg:hidden" : ""}`} />
              </button>
              {isOpen && openSection === "tours" && (
                <ul className="ml-5 mt-1 space-y-1 border-l border-white/10 py-1 pl-3 animate-[fadeIn_180ms_ease-out]">
                  <li><NavLink to="/admin/activities/add" onClick={handleNavigate} className={subLinkClass}>Add Tours</NavLink></li>
                  <li><NavLink to="/admin/activities/fetch" onClick={handleNavigate} className={subLinkClass}>View Tours</NavLink></li>
                </ul>
              )}
            </li>

            <li>
              <NavLink to="/admin/landing-page" onClick={handleNavigate} title="Landing" className={navLinkClass}>
                <LocationMarkerIcon className="h-5 w-5 shrink-0" />
                <span className={!isOpen ? "lg:hidden" : ""}>Landing</span>
              </NavLink>
            </li>

            <li>
              <button type="button" title="Images" onClick={() => toggleSection("images")} className={sectionButtonClass("images")} aria-expanded={openSection === "images"}>
                <span className="flex items-center gap-3"><PhotographIcon className="h-5 w-5 shrink-0" /><span className={!isOpen ? "lg:hidden" : ""}>Images</span></span>
                <ChevronDownIcon className={`h-4 w-4 shrink-0 transition-transform duration-200 ${openSection === "images" ? "rotate-180" : ""} ${!isOpen ? "lg:hidden" : ""}`} />
              </button>
              {isOpen && openSection === "images" && (
                <ul className="ml-5 mt-1 space-y-1 border-l border-white/10 py-1 pl-3 animate-[fadeIn_180ms_ease-out]">
                  <li><NavLink to="/admin/add-explore-images" onClick={handleNavigate} className={subLinkClass}>Explore Images</NavLink></li>
                  <li><NavLink to="/admin/tour-gallery" onClick={handleNavigate} className={subLinkClass}>Tour Gallery</NavLink></li>
                  <li><NavLink to="/admin/route-maps" onClick={handleNavigate} className={subLinkClass}>Route Maps</NavLink></li>
                  <li><NavLink to="/admin/complete-your-day" onClick={handleNavigate} className={subLinkClass}>Complete Your Day</NavLink></li>
                  <li><NavLink to="/admin/frequently-booked-together" onClick={handleNavigate} className={subLinkClass}>Frequently Booked Together</NavLink></li>
                  <li><NavLink to="/admin/explore-more-destinations" onClick={handleNavigate} className={subLinkClass}>Explore More Destinations</NavLink></li>
                </ul>
              )}
            </li>

            <li>
              <button type="button" title="Trip Details" onClick={() => toggleSection("trip-details")} className={sectionButtonClass("trip-details")} aria-expanded={openSection === "trip-details"}>
                <span className="flex items-center gap-3"><ClipboardListIcon className="h-5 w-5 shrink-0" /><span className={!isOpen ? "lg:hidden" : ""}>Trip Details</span></span>
                <ChevronDownIcon className={`h-4 w-4 shrink-0 transition-transform duration-200 ${openSection === "trip-details" ? "rotate-180" : ""} ${!isOpen ? "lg:hidden" : ""}`} />
              </button>
              {isOpen && openSection === "trip-details" && (
                <ul className="ml-5 mt-1 space-y-1 border-l border-white/10 py-1 pl-3 animate-[fadeIn_180ms_ease-out]">
                  <li><NavLink to="/admin/view-tour-details" onClick={handleNavigate} className={subLinkClass}>View Trip Details</NavLink></li>

                </ul>
              )}
            </li>
          </ul>

          <div className="my-5 border-t border-white/10" />

          <NavLink to="/admin/settings" onClick={handleNavigate} title="Settings" className={navLinkClass}>
            <CogIcon className="h-5 w-5 shrink-0" />
            <span className={!isOpen ? "lg:hidden" : ""}>Settings</span>
          </NavLink>
        </nav>

        {/* <div className={`m-3 rounded-xl border border-white/10 bg-white/5 p-3 transition-all ${!isOpen ? "lg:hidden" : ""}`}>
          <p className="text-xs font-semibold text-white">Need a hand?</p>
          <p className="mt-1 text-xs leading-5 text-slate-400">Manage every GoSehari experience from one place.</p>
        </div> */}
      </aside>
    </>
  );
}
